import { postBoardAiEnrichClipboard } from '../api/boardAiApi';
import { useBoardStore } from '../store/useBoardStore';
import { mergeAiCostIntoMeta, normalizeAiCost } from './aiCostFormat';
import {
  MAX_ENRICH_IMAGE_BYTES,
  blobToBase64,
  prepareImageFileForEnrich,
} from './clipboardImageUtils.js';
import {
  buildPasteCanvasEvidence,
  CANVAS_EVIDENCE_SOURCE,
  needsIntentSummary,
  shouldDigestImageShape,
} from './pasteCanvasEvidence.js';

function extensionForMime(mimeType) {
  const subtype = String(mimeType || '').split('/')[1]?.split('+')[0] || 'png';
  if (subtype === 'jpeg') return 'jpg';
  return subtype || 'png';
}

export async function fileFromImageSrc(src, mimeType = 'image/png', name = 'upload') {
  const url = String(src || '').trim();
  if (!url) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob || !blob.size) return null;
    const type = String(blob.type || mimeType || 'image/png').trim() || 'image/png';
    if (!type.startsWith('image/')) return null;
    const ext = extensionForMime(type);
    const base = String(name || 'upload').replace(/\.[^.]+$/, '') || 'upload';
    return new File([blob], `${base}.${ext}`, { type });
  } catch {
    return null;
  }
}

export async function resolveImageFileFromShape(editor, shape) {
  if (!editor || shape?.type !== 'image') return null;
  const assetId = shape.props?.assetId;
  if (!assetId) return null;
  const asset = editor.getAsset?.(assetId);
  const src = asset?.props?.src;
  const mimeType = asset?.props?.mimeType || 'image/png';
  const name = asset?.props?.name || 'upload';
  return fileFromImageSrc(src, mimeType, name);
}

function findImageShapesForAsset(editor, assetId) {
  if (!editor || !assetId) return [];
  return (editor.getCurrentPageShapes?.() || []).filter(
    shape => shape?.type === 'image' && shape.props?.assetId === assetId
  );
}

/**
 * OCR + intentSummary + digest-add for a tldraw image (file picker / drag-drop).
 * Returns false when the asset src is not ready yet (caller should wait).
 */
export async function enrichAndDigestUploadedImage(editor, shapeId, {
  hashtag,
  analyzePinAdded,
} = {}) {
  if (!editor || !shapeId) return false;
  const shape = editor.getShape?.(shapeId);
  if (!shouldDigestImageShape(shape)) return false;
  useBoardStore.getState().setObjectProcessing([{ shapeId }], 'enriching');
  const clearEnriching = () => {
    useBoardStore.getState().clearObjectProcessing(
      [{ shapeId }],
      { onlyPhase: 'enriching' }
    );
  };

  try {
    const imageFile = await resolveImageFileFromShape(editor, shape);
    if (!imageFile) return false;

    const enrichFile = await prepareImageFileForEnrich(imageFile, MAX_ENRICH_IMAGE_BYTES);
    let intentSummary = '';
    let ocrText = '';
    let aiCost = null;
    if (enrichFile && hashtag) {
      try {
        const imageBase64 = await blobToBase64(enrichFile);
        const enriched = await postBoardAiEnrichClipboard(hashtag, {
          kind: 'image',
          imageBase64,
          mimeType: enrichFile.type || imageFile.type || 'image/png',
        });
        intentSummary = String(enriched?.intentSummary || '').trim();
        ocrText = String(enriched?.ocrText || '').trim();
        aiCost = normalizeAiCost(enriched?.aiCost);
      } catch (err) {
        console.warn('[image-digest] enrich failed', err?.message || err);
      }
    }

    const fresh = editor.getShape?.(shapeId);
    if (!fresh || fresh.type !== 'image') return true;
    editor.updateShape({
      id: shapeId,
      type: 'image',
      meta: {
        ...(fresh.meta || {}),
        ...(intentSummary ? { intentSummary } : {}),
        ...(ocrText ? { ocrText } : {}),
        source: CANVAS_EVIDENCE_SOURCE.TLDRAW_IMAGE_UPLOAD,
        imageDigestAttempted: true,
        ...mergeAiCostIntoMeta(fresh.meta || {}, aiCost),
      },
    });

    const evidence = buildPasteCanvasEvidence({
      shapeId,
      kind: 'image',
      intentSummary,
      ocrText,
      source: CANVAS_EVIDENCE_SOURCE.TLDRAW_IMAGE_UPLOAD,
      aiEnriched: Boolean(intentSummary) && needsIntentSummary(ocrText),
    });
    if (evidence && typeof analyzePinAdded === 'function') {
      analyzePinAdded({ canvasEvidence: evidence, placePending: false });
    }
    return true;
  } finally {
    clearEnriching();
  }
}

export function collectImageShapeIdsForDigestEvent(editor, record) {
  if (!editor || !record) return [];
  if (record.typeName === 'shape' && record.type === 'image') {
    return [record.id];
  }
  if (record.typeName === 'asset' && record.type === 'image') {
    return findImageShapesForAsset(editor, record.id).map(shape => shape.id);
  }
  return [];
}
